/**
 * Trilho de servidores, à esquerda de tudo.
 *
 * É a peça que faltava para o LFRCORD deixar de ser uma comunidade só: cada
 * ícone é um servidor de que a pessoa faz parte, e trocar de ícone troca os
 * canais, os cargos e os membros que a lateral mostra. A lista vem do
 * servidor e contém apenas os servidores DELA — nunca todos os que existem.
 */

import { useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { INVITE_CODE_PATTERN, MAX_SERVER_NAME_LENGTH, serverNameError } from "@shared/protocol";
import { pendingFriendRequests, useApp } from "@/state/store";
import { C, FONT_MONO, FONT_SANS, hexA, initialOf } from "@/theme";

const LARGURA = 68;

/** Balão de conversa: o "início", onde moram amigos e conversas diretas. */
function MensagemIcone() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9.6 9.6 0 0 1-2.9-.4L4 21l1.4-4.1A8.2 8.2 0 0 1 3.6 11.5 8.4 8.4 0 0 1 12 3.1a8.4 8.4 0 0 1 9 8.4Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Botao({
  ativo,
  titulo,
  onClick,
  onContextMenu,
  children,
  destaque = false,
}: {
  ativo: boolean;
  titulo: string;
  onClick: () => void;
  onContextMenu?: () => void;
  children: React.ReactNode;
  destaque?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const arredondado = ativo || hover ? 16 : 999;
  return (
    <div style={{ position: "relative", display: "grid", placeItems: "center" }}>
      {/* Pílula indicadora, como no Discord: cresce quando o servidor está aberto. */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          left: -10,
          width: 4,
          height: ativo ? 28 : hover ? 14 : 0,
          borderRadius: 999,
          background: C.text,
          transition: "height 160ms ease",
        }}
      />
      <button
        type="button"
        title={titulo}
        onClick={onClick}
        onContextMenu={
          onContextMenu
            ? event => {
                event.preventDefault();
                onContextMenu();
              }
            : undefined
        }
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          width: 46,
          height: 46,
          display: "grid",
          placeItems: "center",
          padding: 0,
          border: destaque ? `1px dashed ${hover ? C.green : C.lineStrong}` : 0,
          borderRadius: arredondado,
          cursor: "pointer",
          overflow: "hidden",
          background: destaque
            ? "transparent"
            : ativo || hover
              ? "var(--ac, #1F4FD8)"
              : C.raised,
          color: destaque ? (hover ? C.green : C.textMuted) : ativo || hover ? C.ink : C.textSoft,
          fontFamily: FONT_SANS,
          fontSize: 18,
          fontWeight: 700,
          transition: "border-radius 160ms ease, background 160ms ease, color 160ms ease",
        }}
      >
        {children}
      </button>
    </div>
  );
}

const CAMPO: CSSProperties = {
  width: "100%",
  padding: "9px 11px",
  border: `1px solid ${C.lineStrong}`,
  borderRadius: 9,
  background: C.field,
  color: C.text,
  fontSize: 13.5,
  outline: 0,
};

/** Caixa de criar/entrar, aberta pelos botões do rodapé do trilho. */
function Dialogo({
  modo,
  onFechar,
}: {
  modo: "criar" | "entrar";
  onFechar: () => void;
}) {
  const { actions } = useApp();
  const [valor, setValor] = useState("");

  const criando = modo === "criar";
  const problema = criando
    ? valor.length === 0
      ? null
      : serverNameError(valor)
    : valor.length === 0
      ? null
      : INVITE_CODE_PATTERN.test(valor)
        ? null
        : "O código tem de 6 a 8 letras e números.";
  const pronto = valor.trim().length > 0 && problema === null;

  function enviar(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!pronto) return;
    if (criando) actions.createServer(valor);
    else actions.joinServer(valor);
    onFechar();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onFechar}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        display: "grid",
        placeItems: "center",
        background: hexA("#000000", 0.5),
        animation: "fadein 140ms ease",
      }}
    >
      <form
        onClick={event => event.stopPropagation()}
        onSubmit={enviar}
        style={{
          width: "min(400px,92vw)",
          padding: "22px 22px 20px",
          border: `1px solid ${C.lineStrong}`,
          borderRadius: 16,
          background: C.stage,
          display: "grid",
          gap: 12,
          animation: "pop 200ms ease",
        }}
      >
        <div style={{ fontSize: 17, fontWeight: 700 }}>
          {criando ? "Criar um servidor" : "Entrar num servidor"}
        </div>
        <div style={{ fontSize: 12.5, lineHeight: 1.6, color: C.textMuted }}>
          {criando
            ? "Ele nasce com um canal de texto, uma sala de voz e você como dono. Só quem você convidar entra."
            : "Cole o código que alguém do servidor te passou. Sem código, não há como entrar — nem como achar o servidor."}
        </div>

        <input
          value={valor}
          onChange={event =>
            setValor(criando ? event.target.value : event.target.value.toUpperCase())
          }
          placeholder={criando ? "nome do servidor" : "ABCD2345"}
          maxLength={criando ? MAX_SERVER_NAME_LENGTH : 8}
          autoFocus
          spellCheck={false}
          style={{
            ...CAMPO,
            fontFamily: criando ? FONT_SANS : FONT_MONO,
            letterSpacing: criando ? "normal" : ".18em",
            borderColor: problema ? hexA(C.red, 0.5) : C.lineStrong,
          }}
        />
        {problema ? (
          <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: C.red }}>{problema}</span>
        ) : null}

        <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
          <button
            type="submit"
            disabled={!pronto}
            className="hov-bright-108 act-press"
            style={{
              flex: 1,
              padding: "10px 0",
              border: 0,
              borderRadius: 10,
              cursor: pronto ? "pointer" : "default",
              background: "var(--ac, #1F4FD8)",
              color: C.ink,
              fontSize: 13.5,
              fontWeight: 700,
              opacity: pronto ? 1 : 0.55,
            }}
          >
            {criando ? "Criar" : "Entrar"}
          </button>
          <button
            type="button"
            onClick={onFechar}
            style={{
              padding: "10px 16px",
              border: 0,
              borderRadius: 10,
              cursor: "pointer",
              background: "transparent",
              color: C.textMuted,
              fontSize: 13,
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Painel do convite. Fica no trilho porque é a única porta de entrada de um
 * servidor: sem o código, ninguém de fora consegue entrar — nem descobrir que
 * o servidor existe.
 */
function Convite({ serverId, onFechar }: { serverId: string; onFechar: () => void }) {
  const { state, actions } = useApp();
  const [copiado, setCopiado] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  useEffect(() => {
    actions.requestInvite(serverId);
  }, [actions, serverId]);

  const codigo = state.inviteCode?.serverId === serverId ? state.inviteCode.code : null;
  const servidor = state.servers.find(s => s.id === serverId);
  const ehDono = servidor?.ownerId === state.me?.id;

  function copiar(): void {
    if (!codigo) return;
    void navigator.clipboard.writeText(codigo).then(
      () => {
        setCopiado(true);
        window.setTimeout(() => setCopiado(false), 2000);
      },
      () => actions.setNotice("Não consegui copiar. Selecione o código e copie na mão."),
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onFechar}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        display: "grid",
        placeItems: "center",
        background: hexA("#000000", 0.5),
        animation: "fadein 140ms ease",
      }}
    >
      <div
        onClick={event => event.stopPropagation()}
        style={{
          width: "min(420px,92vw)",
          padding: "22px",
          border: `1px solid ${C.lineStrong}`,
          borderRadius: 16,
          background: C.stage,
          display: "grid",
          gap: 12,
          animation: "pop 200ms ease",
        }}
      >
        <div style={{ fontSize: 17, fontWeight: 700 }}>
          Convidar para {servidor?.name ?? "o servidor"}
        </div>
        <div style={{ fontSize: 12.5, lineHeight: 1.6, color: C.textMuted }}>
          Quem tiver este código entra. Quem não tiver não entra — e nem consegue ver que o
          servidor existe.
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 14px",
            border: `1px solid ${C.lineStrong}`,
            borderRadius: 10,
            background: C.field,
          }}
        >
          <span
            style={{
              flex: 1,
              fontFamily: FONT_MONO,
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: ".22em",
              color: codigo ? C.text : C.textGhost,
              userSelect: "all",
            }}
          >
            {codigo ?? "········"}
          </span>
          <button
            type="button"
            onClick={copiar}
            disabled={!codigo}
            className="act-press"
            style={{
              padding: "8px 14px",
              border: 0,
              borderRadius: 8,
              cursor: codigo ? "pointer" : "default",
              background: copiado ? C.green : "var(--ac, #1F4FD8)",
              color: C.ink,
              fontSize: 12.5,
              fontWeight: 700,
              opacity: codigo ? 1 : 0.5,
              transition: "background 160ms ease",
            }}
          >
            {copiado ? "Copiado" : "Copiar"}
          </button>
        </div>

        {ehDono ? (
          <div style={{ borderTop: `1px solid ${C.lineSoft}`, marginTop: 4, paddingTop: 14 }}>
            <div style={{ fontSize: 12.5, lineHeight: 1.6, color: C.textMuted, marginBottom: 10 }}>
              Apagar o servidor leva junto os canais, as salas de voz, os cargos e todas as
              mensagens escritas nele. Não tem como desfazer.
            </div>
            {confirmando ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => {
                    actions.deleteServer(serverId);
                    onFechar();
                  }}
                  style={{
                    padding: "9px 16px",
                    border: `1px solid ${hexA(C.red, 0.5)}`,
                    borderRadius: 9,
                    cursor: "pointer",
                    background: hexA(C.red, 0.16),
                    color: C.red,
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  Apagar {servidor?.name ?? "o servidor"} para sempre
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmando(false)}
                  style={{
                    padding: "9px 14px",
                    border: 0,
                    borderRadius: 9,
                    cursor: "pointer",
                    background: "transparent",
                    color: C.textMuted,
                    fontSize: 13,
                  }}
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmando(true)}
                style={{
                  padding: "9px 16px",
                  border: `1px solid ${hexA(C.red, 0.4)}`,
                  borderRadius: 9,
                  cursor: "pointer",
                  background: "transparent",
                  color: C.red,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Apagar servidor
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              actions.leaveServer(serverId);
              onFechar();
            }}
            style={{
              padding: "9px 0",
              border: 0,
              borderRadius: 9,
              cursor: "pointer",
              background: "transparent",
              color: C.red,
              fontSize: 12.5,
            }}
          >
            Sair deste servidor
          </button>
        )}

        <button
          type="button"
          onClick={onFechar}
          style={{
            padding: "9px 0",
            border: 0,
            borderRadius: 9,
            cursor: "pointer",
            background: "transparent",
            color: C.textMuted,
            fontSize: 13,
          }}
        >
          Fechar
        </button>
      </div>
    </div>
  );
}

export function ServerRail() {
  const { state, actions } = useApp();
  const [dialogo, setDialogo] = useState<"criar" | "entrar" | null>(null);
  const [convidando, setConvidando] = useState<string | null>(null);

  const pedidos = pendingFriendRequests(state).length;
  const emMensagens = state.tab === "diretas";

  // Sem a migração multi-servidor não há servidores para listar, mas o botão
  // de Mensagens continua fazendo sentido — é por ele que se chega às
  // solicitações de amizade.
  const temServidores = state.serversAvailable;

  return (
    <>
      <div
        style={{
          width: LARGURA,
          flex: `0 0 ${LARGURA}px`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          padding: "12px 0",
          background: C.app,
          borderRight: `1px solid ${C.lineSoft}`,
          overflowY: "auto",
        }}
      >
        {/*
          Botão de Mensagens, no topo — é daqui que se chega às solicitações
          de amizade. Sem ele, o painel ficava escondido atrás de uma aba
          dentro da lateral, e quem recebia um pedido não achava onde aceitar.
        */}
        <div style={{ position: "relative", display: "grid", placeItems: "center" }}>
          <Botao
            ativo={emMensagens}
            titulo={
              pedidos > 0
                ? `Mensagens · ${pedidos} solicitação(ões) de amizade esperando`
                : "Mensagens e amigos"
            }
            onClick={() => actions.setTab("diretas")}
          >
            <MensagemIcone />
          </Botao>
          {pedidos > 0 ? (
            <span
              aria-label={`${pedidos} solicitação(ões) de amizade`}
              style={{
                position: "absolute",
                right: 4,
                bottom: 0,
                minWidth: 18,
                height: 18,
                display: "grid",
                placeItems: "center",
                padding: "0 5px",
                borderRadius: 999,
                border: `2px solid ${C.app}`,
                background: C.red,
                color: C.text,
                fontFamily: FONT_MONO,
                fontSize: 10,
                fontWeight: 700,
                pointerEvents: "none",
              }}
            >
              {pedidos}
            </span>
          ) : null}
        </div>

        <div style={{ width: 26, height: 1, background: C.lineSoft, margin: "4px 0" }} />

        {temServidores &&
          state.servers.map(server => (
          <Botao
            key={server.id}
            // Só fica aceso quando a lateral está mostrando este servidor —
            // estando em Mensagens, nenhum servidor está "aberto".
            ativo={!emMensagens && server.id === state.activeServerId}
            titulo={`${server.name} · ${server.memberCount} membro(s) · botão direito para convidar`}
            onClick={() => {
              // Voltar para a visão de canais faz parte de abrir o servidor:
              // sem isto, clicar num servidor estando em Mensagens trocava o
              // conteúdo por baixo e a lateral continuava nas conversas.
              actions.setTab("canais");
              actions.selectServer(server.id);
            }}
            onContextMenu={() => setConvidando(server.id)}
          >
            {server.iconUrl ? (
              <img
                src={server.iconUrl}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              initialOf(server.name)
            )}
          </Botao>
        ))}

        {temServidores ? (
          <>
            <Botao
              ativo={false}
              destaque
              titulo="Criar um servidor"
              onClick={() => setDialogo("criar")}
            >
              +
            </Botao>
            <Botao
              ativo={false}
              destaque
              titulo="Entrar com um código de convite"
              onClick={() => setDialogo("entrar")}
            >
              <span style={{ fontFamily: FONT_MONO, fontSize: 15 }}>#</span>
            </Botao>
          </>
        ) : null}
      </div>

      {dialogo ? <Dialogo modo={dialogo} onFechar={() => setDialogo(null)} /> : null}
      {convidando ? (
        <Convite serverId={convidando} onFechar={() => setConvidando(null)} />
      ) : null}
    </>
  );
}
